import { useState, useEffect, useCallback } from "react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import {
	getCurrentMonthRange,
	DATE_PRESETS,
} from "../../utils/dashboardDatePresets";

const defaultRange = getCurrentMonthRange();

const useDashboardMetrics = ({ queueIds } = {}) => {
	const [loading, setLoading] = useState(true);
	const [dateFrom, setDateFrom] = useState(defaultRange.from);
	const [dateTo, setDateTo] = useState(defaultRange.to);
	const [agentId, setAgentId] = useState("");
	const [activePreset, setActivePreset] = useState("currentMonth");
	const [metrics, setMetrics] = useState(null);

	const fetchMetrics = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/dashboard/metrics", {
				params: {
					dateFrom,
					dateTo,
					agentId: agentId || undefined,
					queueIds: queueIds?.length ? JSON.stringify(queueIds) : undefined,
				},
			});
			setMetrics(data);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, [dateFrom, dateTo, agentId, queueIds]);

	const applyPreset = useCallback(presetKey => {
		const preset = DATE_PRESETS[presetKey];
		if (!preset) return;

		const range = preset();
		setDateFrom(range.from);
		setDateTo(range.to);
		setActivePreset(presetKey);
	}, []);

	useEffect(() => {
		const delay = setTimeout(() => {
			fetchMetrics();
		}, 300);
		return () => clearTimeout(delay);
	}, [fetchMetrics]);

	useEffect(() => {
		const socket = openSocket();

		const handlePresence = ({ onlineUserIds }) => {
			setMetrics(prev => {
				if (!prev?.presence) return prev;
				const onlineSet = new Set(onlineUserIds || []);
				return {
					...prev,
					presence: prev.presence.map(user => ({
						...user,
						online: onlineSet.has(user.userId),
					})),
				};
			});
		};

		socket.on("userPresence", handlePresence);

		return () => {
			socket.off("userPresence", handlePresence);
		};
	}, []);

	const handleDateFromChange = value => {
		setDateFrom(value);
		setActivePreset("");
	};

	const handleDateToChange = value => {
		setDateTo(value);
		setActivePreset("");
	};

	const handleAgentChange = value => {
		setAgentId(value);
	};

	return {
		loading,
		metrics,
		dateFrom,
		dateTo,
		agentId,
		activePreset,
		setDateFrom: handleDateFromChange,
		setDateTo: handleDateToChange,
		setAgentId: handleAgentChange,
		applyPreset,
		refresh: fetchMetrics,
	};
};

export default useDashboardMetrics;
